import React, { useState, useEffect } from 'react';
import { useThemeStore, useAppStore, useSOWListDataStore, useTranslation, useTabStore, useSOWDataStore, useSOWTasksStore } from '../../store';
import { decodeHtml } from '../../services/utils';
import Loading from '../Loading';
import LoadingError from '../LoadingError';
import '../../css/Home.css';
import { CONSTANTS } from '../../constants/constants';
import { queryParamsService } from '../../services/queryParams';
import { isValueEmpty } from '../../services/utils';
import { summarizeTasksStatus } from '../../services/tasksStatus';
import { readStoredTicketTasksByKey } from '../../store/ticketTasksStorage';
import NoData from '../NoData';

const HomeTab = () => {
  const { sowListData, sowListLoading, sowListError, fetchSOWListData } = useSOWListDataStore();
  const { getCacheKeyString } = queryParamsService;
  const { getTasksStatus, jobsTasks } = useSOWTasksStore();
  const { getSOWName } = useSOWDataStore();
  const { issueKey } = queryParamsService.getParams();
  const { activeTab, setActiveTab } = useTabStore();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, open, ongoing, completed
  const [sortField, setSortField] = useState('workorderKey');
  const [sortDirection, setSortDirection] = useState('asc');
  // Local progress of tickets other than the open one. Each ticket persists to its own
  // record, so only the open ticket's tasks are in memory.
  const [storedStatuses, setStoredStatuses] = useState({});
  // Bumped when the page becomes visible again: another tab may have recorded progress
  // on a ticket while this one sat in the background.
  const [visibilityTick, setVisibilityTick] = useState(0);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setVisibilityTick((tick) => tick + 1);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Re-read whenever the list refreshes or the page comes back to the foreground, so the
  // badges never show progress that another tab has since changed.
  useEffect(() => {
    if (!sowListData) return undefined;

    let isCancelled = false;
    const needsLocalStatus = (job) => isValueEmpty(job.tasksStatus) || job.tasksStatus === CONSTANTS.STATUS.NOT_STARTED;

    const loadStoredStatuses = async () => {
      const keys = sowListData.filter(needsLocalStatus).map((job) => getCacheKeyString(job.workorderKey, job.token));
      let statuses;
      try {
        const storedTasks = await readStoredTicketTasksByKey(keys);
        statuses = Object.fromEntries(keys.map((key) => [key, summarizeTasksStatus(storedTasks[key])]));
      } catch (error) {
        // The keys embed ticket tokens, so none are logged.
        console.error('Failed to read stored tasks for the work order list:', error);
        statuses = Object.fromEntries(keys.map((key) => [key, CONSTANTS.STATUS.NOT_STARTED]));
      }
      if (!isCancelled) {
        setStoredStatuses(statuses);
      }
    };

    loadStoredStatuses();
    return () => { isCancelled = true; };
  }, [sowListData, visibilityTick]);

  useEffect(() => {
    queryParamsService.initialize();
    // The store records the failure in sowListError, which the tab renders
    const loadList = async () => {
      try {
        await fetchSOWListData();
      } catch (error) {
        console.error('Failed to load SOW list:', error);
      }
    };
    loadList();
  }, []);
  
  const getSOWKey = () => {
      let name = getSOWName();
      if (isValueEmpty(name)) {
          name = issueKey;
      }
      return name;
  };
  
  const getActiveCardClass = (jobKey) => {
    if (jobKey === getSOWKey()) {
        return 'job-card active';
    }
    return 'job-card';
  };
  
  // Determine job status based on maintenance window date
  const getJobStatus = (job) => {

    let jobStatus = CONSTANTS.STATUS.IN_PROGRESS;
    if (isValueEmpty(job.tasksStatus) || job.tasksStatus === CONSTANTS.STATUS.NOT_STARTED) {
        jobStatus = CONSTANTS.STATUS.NOT_STARTED;
    }
    
    if (!isValueEmpty(job.tasksStatus) &&
        job.tasksStatus === CONSTANTS.STATUS.COMPLETED) {
        return CONSTANTS.STATUS.SUBMITTED;
    }
    
    if (jobStatus === CONSTANTS.STATUS.NOT_STARTED) {
        const jobKey = getCacheKeyString(job.workorderKey, job.token);
        const tasksStatus = isValueEmpty(jobsTasks[jobKey])
          ? (storedStatuses[jobKey] ?? CONSTANTS.STATUS.NOT_STARTED)
          : getTasksStatus(jobKey);

        if (tasksStatus !== CONSTANTS.STATUS.NOT_STARTED) {
            jobStatus = CONSTANTS.STATUS.IN_PROGRESS;
        }
    }

    return jobStatus
  };
  
  // Filter and sort jobs
  const filteredAndSortedJobs = React.useMemo(() => {
    if (!sowListData) return [];
    
    let jobs = [...sowListData];
    
    // Apply status filter
    if (statusFilter !== 'all') {
      jobs = jobs.filter(job => getJobStatus(job) === statusFilter);
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      jobs = jobs.filter(job => 
        job.workorderKey.toLowerCase().includes(term) ||
        job.customer?.customerName?.toLowerCase().includes(term) ||
        job.customer?.customerCompany?.toLowerCase().includes(term) ||
        job.customer?.customerCity?.toLowerCase().includes(term) ||
        job.maintenanceWindow?.pointOfContact?.toLowerCase().includes(term)
      );
    }
    
    // Apply sorting
    jobs.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'customerName':
          aValue = a.customer?.customerName || '';
          bValue = b.customer?.customerName || '';
          break;
        case 'company':
          aValue = a.customer?.customerCompany || '';
          bValue = b.customer?.customerCompany || '';
          break;
        case 'serviceDate':
          aValue = a.maintenanceWindow?.servicingDate ? 
            new Date(...a.maintenanceWindow.servicingDate).getTime() : 0;
          bValue = b.maintenanceWindow?.servicingDate ? 
            new Date(...b.maintenanceWindow.servicingDate).getTime() : 0;
          break;
        case 'status':
          aValue = getJobStatus(a);
          bValue = getJobStatus(b);
          break;
        default: // workorderKey
          aValue = a.workorderKey || '';
          bValue = b.workorderKey || '';
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return jobs;
  }, [sowListData, statusFilter, searchTerm, sortField, sortDirection, storedStatuses, jobsTasks]);
  
  const handleJobClick = (workorderKey, token, e) => {
    e.preventDefault();

    // Create new search params
    const searchParams = new URLSearchParams();
    searchParams.set('token', token);
    searchParams.set('issueKey', workorderKey);
    
    const newUrl = `${window.location.pathname}?${searchParams.toString()}#${CONSTANTS.TABS.DESCRIPTION}`;
    window.history.pushState({}, '', newUrl);
    
    setActiveTab(CONSTANTS.TABS.DESCRIPTION);
  };

  const formatServiceDate = (servicingDate) => {
    if (!servicingDate || servicingDate.length < 5) return 'N/A';
    const [year, month, day, hour, minute] = servicingDate;
    return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };
  
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case CONSTANTS.STATUS.NOT_STARTED: return 'status-open';
      case CONSTANTS.STATUS.IN_PROGRESS: return 'status-ongoing';
      case CONSTANTS.STATUS.SUBMITTED: return 'status-completed';
      default: return 'status-unknown';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case CONSTANTS.STATUS.NOT_STARTED: return t(CONSTANTS.STATUS.NOT_STARTED);
      case CONSTANTS.STATUS.IN_PROGRESS: return t(CONSTANTS.STATUS.IN_PROGRESS);
      case CONSTANTS.STATUS.SUBMITTED: return t(CONSTANTS.STATUS.SUBMITTED);
      default: return t('unknown');
    }
  };
  
  const statusCounts = React.useMemo(() => {
    let counts = { total: 0 };
    counts[CONSTANTS.STATUS.NOT_STARTED] = 0;
    counts[CONSTANTS.STATUS.IN_PROGRESS] = 0;
    counts[CONSTANTS.STATUS.SUBMITTED] = 0;
    
    if (!sowListData) return counts;

    counts.total = sowListData.length
    
    sowListData.forEach(job => {
      const status = getJobStatus(job);
      counts[status]++;
    });
    
    return counts;
  }, [sowListData, storedStatuses, jobsTasks]);
  
  if (sowListLoading) {
    return (
      <Loading message={t('loadingWOs')}/>
    );
  }
  
  if (sowListError) {
    return (
      <LoadingError message={t('errorLoadingWOs', {'error': sowListError})}/>
    );
  }
  
  if (!sowListData) {
    return (
      <NoData/>
    );
  }
  
  return (
    <div className="tab-content home-tab">
      <div className="home-header">
        <h2>{t('workOrdersDashboard')}</h2>
        <p>{t('workOrdersDashboardDesc')}</p>
      </div>
      
      {/* Statistics Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-content">
            <span className="stat-number">{statusCounts.total}</span>
            <span className="stat-label">{t('totalJobs')}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <span className="stat-number">{statusCounts[CONSTANTS.STATUS.NOT_STARTED]}</span>
            <span className="stat-label">{t(CONSTANTS.STATUS.NOT_STARTED)}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <span className="stat-number">{statusCounts[CONSTANTS.STATUS.IN_PROGRESS]}</span>
            <span className="stat-label">{t(CONSTANTS.STATUS.IN_PROGRESS)}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <span className="stat-number">{statusCounts[CONSTANTS.STATUS.SUBMITTED]}</span>
            <span className="stat-label">{t(CONSTANTS.STATUS.SUBMITTED)}</span>
          </div>
        </div>
      </div>
      
      {/* Controls */}
      <div className="jobs-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('searchWOPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-controls">
          <label>{t('status')}</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('allJobs')} ({statusCounts.total})</option>
            <option value={CONSTANTS.STATUS.NOT_STARTED}>{t(CONSTANTS.STATUS.NOT_STARTED)} ({statusCounts[CONSTANTS.STATUS.NOT_STARTED]})</option>
            <option value={CONSTANTS.STATUS.IN_PROGRESS}>{t(CONSTANTS.STATUS.IN_PROGRESS)} ({statusCounts[CONSTANTS.STATUS.IN_PROGRESS]})</option>
            <option value={CONSTANTS.STATUS.SUBMITTED}>{t(CONSTANTS.STATUS.SUBMITTED)} ({statusCounts[CONSTANTS.STATUS.SUBMITTED]})</option>
          </select>
        </div>
        
        <div className="sort-controls">
          <label>{t('sortBy')}</label>
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="sort-select"
          >
            <option value="workorderKey">{t('workOrderNo')}</option>
            <option value="customerName">{t('customerName')}</option>
            <option value="company">{t('company')}</option>
            <option value="serviceDate">{t('maintenanceWindow')}</option>
            <option value="status">{t('statuso')}</option>
          </select>
          
          <button
            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            className="sort-direction-btn"
          >
            {sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>
      
      {/* Jobs List */}
      <div className="jobs-list">
        <div className="jobs-header">
          <span className="jobs-count">
            {t('jobsFound', {'found': filteredAndSortedJobs.length, 'plural': filteredAndSortedJobs.length !== 1 ? 's' : ''})}
          </span>
        </div>
        
        {filteredAndSortedJobs.length === 0 ? (
          <div className="no-jobs">
            {searchTerm || statusFilter !== 'all' 
              ? t('noJobMatch')
              : t('noJobAvailable')
            }
          </div>
        ) : (
          <div className="jobs-grid">
            {filteredAndSortedJobs.map((job) => {
              const status = getJobStatus(job);
              
              return (
                <div 
                  key={job.workorderKey} 
                  className={getActiveCardClass(job.workorderKey)}
                  onClick={(e) => handleJobClick(job.workorderKey, job.token, e)}
                >
                  <div className="job-header">
                    <h3 className="job-number">{job.workorderKey}</h3>
                    <span className={`status-badge ${getStatusBadgeClass(status)}`}>
                      {getStatusText(status)}
                    </span>
                  </div>
                  
                  <div className="job-content">
                    <div className="job-detail">
                      <label>{t('customer')}</label>
                      <span>{job.customer?.customerName || t('na')}</span>
                    </div>
                    
                    <div className="job-detail">
                      <label>{t('company')}</label>
                      <span>{job.customer?.customerCompany || t('na')}</span>
                    </div>
                    
                    <div className="job-detail">
                      <label>{t('location')}</label>
                      <span>
                        {job.customer?.customerCity || t('na')}, {job.customer?.customerStateProvince || ''}
                      </span>
                    </div>
                    
                    <div className="job-detail">
                      <label>{t('maintenanceWindow')}</label>
                      <span>{formatServiceDate(job.maintenanceWindow?.servicingDate)}</span>
                    </div>
                    
                    <div className="job-detail">
                      <label>{t('pointOfContact')}</label>
                      <span>{job.maintenanceWindow?.pointOfContact || t('na')}</span>
                    </div>
                    
                    {job.productInformation && (
                      <div className="job-detail">
                        <label>{t('productInformation')}</label>
                        <span>
                          {job.productInformation.deviceType} ({job.productInformation.ciaraSerial})
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="job-footer">
                    <span className="view-details">{t('clickForDetails')} →</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Refresh Button */}
      <div className="refresh-section">
        <button onClick={fetchSOWListData} className="refresh-btn">
          <span className="glyphicon glyphicon-refresh"></span> {t('refreshList')}
        </button>
      </div>
    </div>
  );
};

export default HomeTab;